/**
 * Per-shape placeholder factory functions.
 *
 * Slice D — every shipped ability gets visible 3D feedback on cast/toggle/
 * channel. Three.js primitives only (no GLSL shaders, no particles); bespoke
 * VFX is the post-v1 polish pass.
 *
 * Each factory returns a `VfxInstance` with three obligations:
 *   - `object`: the THREE.Object3D to add to the scene.
 *   - `tick(dtMs)`: advance internal animation by `dtMs`; return `true` while
 *     the visual should remain in the scene, `false` once it has lived out
 *     its TTL and should be removed by the dispatcher.
 *   - `dispose()`: dispose geometry + material handles so GC can reclaim.
 *     Called by the dispatcher AFTER it removes the object from the scene
 *     (either because `tick` returned false, or a stop event arrived for a
 *     persistent toggle/channel shape).
 *
 * The dispatcher (`cast-vfx.ts`) owns the lifecycle — factories never touch
 * the scene graph themselves.
 */

import * as THREE from "three";

export interface VfxInstance {
  readonly object: THREE.Object3D;
  /** Returns true while alive, false once TTL elapsed. */
  tick(dtMs: number): boolean;
  /** Free geometry + material handles. Called by the dispatcher AFTER it
   *  removes `object` from the scene. */
  dispose(): void;
}

/** Common dispose helper — release geometry + material from a Mesh tree. */
function disposeMaterialTree(root: THREE.Object3D): void {
  root.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    if (mesh.material) {
      const mat = mesh.material as THREE.Material | THREE.Material[];
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat.dispose();
    }
  });
}

// ---------------------------------------------------------------------------
// self-aura
// ---------------------------------------------------------------------------
// Translucent sphere around the caster, fades over `durationMs`. Anchored at
// the caster's position at cast time; doesn't follow if the caster moves.

export function createSelfAura(opts: {
  color: number; durationMs: number; radius?: number;
}): VfxInstance {
  const radius = opts.radius ?? 1.5;
  const geom = new THREE.SphereGeometry(radius, 16, 12);
  const mat = new THREE.MeshBasicMaterial({
    color: opts.color, transparent: true, opacity: 0.45, depthWrite: false,
  });
  const mesh = new THREE.Mesh(geom, mat);
  let elapsed = 0;
  return {
    object: mesh,
    tick(dtMs) {
      elapsed += dtMs;
      const t = elapsed / opts.durationMs;
      if (t >= 1) return false;
      mat.opacity = 0.45 * (1 - t);
      const s = 1 + 0.3 * t;
      mesh.scale.setScalar(s);
      return true;
    },
    dispose() { disposeMaterialTree(mesh); },
  };
}

// ---------------------------------------------------------------------------
// point-burst
// ---------------------------------------------------------------------------
// Ground ring decal that expands from 0 → `radius` and fades. ~0.5-0.9s TTL.

export function createPointBurst(opts: {
  color: number; radius: number; durationMs?: number;
}): VfxInstance {
  const durationMs = opts.durationMs ?? 700;
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(opts.radius * 0.6, opts.radius, 32),
    new THREE.MeshBasicMaterial({
      color: opts.color, transparent: true, opacity: 0.8,
      side: THREE.DoubleSide, depthWrite: false,
    }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.05;
  let elapsed = 0;
  return {
    object: ring,
    tick(dtMs) {
      elapsed += dtMs;
      const t = elapsed / durationMs;
      if (t >= 1) return false;
      const s = 0.2 + t * 0.8;
      ring.scale.setScalar(s);
      (ring.material as THREE.MeshBasicMaterial).opacity = 0.8 * (1 - t);
      return true;
    },
    dispose() { disposeMaterialTree(ring); },
  };
}

// ---------------------------------------------------------------------------
// unit-tag
// ---------------------------------------------------------------------------
// Glyph floating above the target unit, fades over duration. Sized to read
// from the default MOBA camera pitch.

export function createUnitTag(opts: {
  color: number; durationMs: number;
}): VfxInstance {
  const group = new THREE.Group();
  const sprite = new THREE.Mesh(
    new THREE.PlaneGeometry(1.0, 1.0),
    new THREE.MeshBasicMaterial({
      color: opts.color, transparent: true, opacity: 0.9, depthWrite: false,
    }),
  );
  sprite.position.y = 3;
  // Tilt to face camera roughly (camera looks down at ~40°). Cheaper than
  // billboarding every frame for a fade-only placeholder.
  sprite.rotation.x = -Math.PI / 4;
  group.add(sprite);

  let elapsed = 0;
  return {
    object: group,
    tick(dtMs) {
      elapsed += dtMs;
      const t = elapsed / opts.durationMs;
      if (t >= 1) return false;
      const mat = sprite.material as THREE.MeshBasicMaterial;
      // Quick fade-in over first 10%, slow fade-out across last 30%.
      mat.opacity = t < 0.1 ? 0.9 * (t / 0.1)
        : t > 0.7 ? 0.9 * ((1 - t) / 0.3)
        : 0.9;
      // Slow bob.
      sprite.position.y = 3 + Math.sin(elapsed / 250) * 0.15;
      return true;
    },
    dispose() { disposeMaterialTree(group); },
  };
}

// ---------------------------------------------------------------------------
// zone-disc
// ---------------------------------------------------------------------------
// Persistent translucent disc at the cast point, scaled to `radius`, alive
// for the zone's full duration. Pulses faintly to look "active" rather than
// static decal.

export function createZoneDisc(opts: {
  color: number; radius: number; durationMs: number;
}): VfxInstance {
  const disc = new THREE.Mesh(
    new THREE.CircleGeometry(opts.radius, 48),
    new THREE.MeshBasicMaterial({
      color: opts.color, transparent: true, opacity: 0.35,
      side: THREE.DoubleSide, depthWrite: false,
    }),
  );
  disc.rotation.x = -Math.PI / 2;
  disc.position.y = 0.05;
  let elapsed = 0;
  return {
    object: disc,
    tick(dtMs) {
      elapsed += dtMs;
      const t = elapsed / opts.durationMs;
      if (t >= 1) return false;
      const mat = disc.material as THREE.MeshBasicMaterial;
      // Hold full opacity until last 20% then fade.
      const base = t > 0.8 ? 0.35 * ((1 - t) / 0.2) : 0.35;
      // Faint pulse.
      mat.opacity = base * (0.85 + 0.15 * Math.sin(elapsed / 200));
      return true;
    },
    dispose() { disposeMaterialTree(disc); },
  };
}

// ---------------------------------------------------------------------------
// beam
// ---------------------------------------------------------------------------
// Stretched cylinder from cast origin towards the target point. Brief flash;
// orientation set at construction via `from` → `to` vector.

export function createBeam(opts: {
  color: number; from: THREE.Vector3; to: THREE.Vector3; durationMs?: number;
}): VfxInstance {
  const durationMs = opts.durationMs ?? 600;
  const dir = new THREE.Vector3().subVectors(opts.to, opts.from);
  const length = Math.max(0.5, dir.length());
  const geom = new THREE.CylinderGeometry(0.2, 0.2, length, 12, 1, true);
  const mat = new THREE.MeshBasicMaterial({
    color: opts.color, transparent: true, opacity: 0.8, depthWrite: false,
  });
  const mesh = new THREE.Mesh(geom, mat);
  // Place midpoint, orient along (from → to).
  mesh.position.copy(opts.from).addScaledVector(dir, 0.5);
  mesh.position.y = Math.max(mesh.position.y, 1);
  mesh.quaternion.setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    dir.clone().normalize(),
  );
  let elapsed = 0;
  return {
    object: mesh,
    tick(dtMs) {
      elapsed += dtMs;
      const t = elapsed / durationMs;
      if (t >= 1) return false;
      mat.opacity = 0.8 * (1 - t);
      return true;
    },
    dispose() { disposeMaterialTree(mesh); },
  };
}

// ---------------------------------------------------------------------------
// channel-orb
// ---------------------------------------------------------------------------
// Persistent pulsing orb above the caster. Lives until `dispose()` is called
// by the dispatcher (when `channelStopped` arrives) — its `tick` always
// returns true.

export function createChannelOrb(opts: { color: number }): VfxInstance {
  const orb = new THREE.Mesh(
    new THREE.SphereGeometry(0.4, 20, 14),
    new THREE.MeshBasicMaterial({
      color: opts.color, transparent: true, opacity: 0.8, depthWrite: false,
    }),
  );
  orb.position.y = 3.2;
  let elapsed = 0;
  return {
    object: orb,
    tick(dtMs) {
      elapsed += dtMs;
      const pulse = 0.7 + 0.3 * Math.sin(elapsed / 180);
      orb.scale.setScalar(pulse);
      (orb.material as THREE.MeshBasicMaterial).opacity = 0.6 + 0.3 * pulse;
      return true;
    },
    dispose() { disposeMaterialTree(orb); },
  };
}

// ---------------------------------------------------------------------------
// toggle-aura
// ---------------------------------------------------------------------------
// Persistent swirling torus around the caster. Lives until `dispose()` is
// called by the dispatcher (when `toggleStopped` arrives).

export function createToggleAura(opts: { color: number }): VfxInstance {
  const torus = new THREE.Mesh(
    new THREE.TorusGeometry(1.0, 0.12, 12, 32),
    new THREE.MeshBasicMaterial({
      color: opts.color, transparent: true, opacity: 0.7, depthWrite: false,
    }),
  );
  torus.rotation.x = Math.PI / 2;
  torus.position.y = 0.5;
  let elapsed = 0;
  return {
    object: torus,
    tick(dtMs) {
      elapsed += dtMs;
      torus.rotation.z = elapsed / 600;
      // Subtle vertical bob.
      torus.position.y = 0.5 + Math.sin(elapsed / 350) * 0.08;
      return true;
    },
    dispose() { disposeMaterialTree(torus); },
  };
}
